import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";

const CartContext = createContext(null);
const STORAGE_KEY = "vitae_cart";

export const itemKey = (modelId, size, finish) => `${modelId}__${size}__${finish}`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // product must include: key, model_id, model_name, size, size_label,
  // finish, finish_label, price, image, slug
  const addItem = useCallback((product) => {
    // Count "added to cart" once per browser session (admin stats).
    if (!sessionStorage.getItem("vitae_cart_tracked")) {
      sessionStorage.setItem("vitae_cart_tracked", "1");
      api.post("/track/cart").catch(() => {});
    }
    setItems((prev) => {
      const existing = prev.find((i) => i.key === product.key);
      if (existing) {
        return prev.map((i) =>
          i.key === product.key ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    setOpen(true);
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const updateQty = useCallback((key, quantity) => {
    setItems((prev) =>
      prev
        .map((i) => (i.key === key ? { ...i, quantity } : i))
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const total = items.reduce((s, i) => s + i.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, updateQty, clear, count, total, open, setOpen }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
